import { TradeItem, LocalTradeFilters } from '@/types';
import { getEffectiveSettlementCurrency, getNativeAmount } from './settlement-ccy';
import { getEffectiveTradeStatus } from './effective-status';

export interface ApplyLocalTradeFiltersOptions {
  /**
   * Skip the trade-type predicate. The MetricsBar uses this so its category
   * metrics react to every other filter (security, currency, status, …) yet
   * stay stable while a `tradeTypes` filter narrows the table — the STOCK L/B
   * and REPO sections of the bar ARE the trade-type selector, so they must keep
   * showing every category regardless of which type is selected (LR-469).
   */
  skipTradeTypes?: boolean;
}

/**
 * Apply the structural local-filter predicate to a list of trades.
 *
 * The Trades tab and the MTM tab share it as one source of truth for "which rows
 * survive the sidebar filters". Search and sorting are server-side; this function
 * is filtering only.
 *
 * Note on the counterparty filter: MtmPage never populates `counterparties` (the
 * active subtab fixes the counterparty), so the counterparty branch is a no-op
 * there. Keeping it here means the predicate is identical for both tabs.
 */
export function applyLocalTradeFilters(
  data: TradeItem[],
  filters: LocalTradeFilters,
  options: ApplyLocalTradeFiltersOptions = {},
): TradeItem[] {
  let result = data;

  if (filters.statuses.length > 0) {
    // LR-497: match on the derived effective status so the two computed buckets
    // (Partially Settled / Pending Partial Settlement) are selectable. Buckets are
    // mutually exclusive — an FOP one-leg trade is `partially_settled`, NOT
    // `pending_open_settlement`; an active trade with a pending recall is
    // `pending_partial_settlement`, NOT `active`.
    result = result.filter((item) => filters.statuses.includes(getEffectiveTradeStatus(item)));
  }

  if (filters.counterparties.length > 0) {
    result = result.filter((item) => filters.counterparties.includes(item.counterparty));
  }

  if (filters.symbols.length > 0) {
    result = result.filter((item) => filters.symbols.includes(item.symbol_id));
  }

  if (!options.skipTradeTypes && filters.tradeTypes.length > 0) {
    result = result.filter((item) => filters.tradeTypes.includes(item.trade_type));
  }

  if (filters.collateralTypes.length > 0) {
    result = result.filter((item) => item.collateral_type && filters.collateralTypes.includes(item.collateral_type));
  }

  if (filters.agreementTypes.length > 0) {
    result = result.filter((item) => filters.agreementTypes.includes(item.agreement));
  }

  if (filters.tradeDateFrom) {
    result = result.filter((item) => item.trade_date >= filters.tradeDateFrom!);
  }

  if (filters.tradeDateTo) {
    result = result.filter((item) => item.trade_date <= filters.tradeDateTo!);
  }

  if (filters.settlementDateFrom) {
    result = result.filter((item) => item.settlement_date >= filters.settlementDateFrom!);
  }

  if (filters.settlementDateTo) {
    result = result.filter((item) => item.settlement_date <= filters.settlementDateTo!);
  }

  if (filters.quantityMin !== null) {
    result = result.filter((item) => item.quantity >= filters.quantityMin!);
  }

  if (filters.quantityMax !== null) {
    result = result.filter((item) => item.quantity <= filters.quantityMax!);
  }

  // LR-522: the (renamed) "Trade Margin Value" column displays the BOOKING
  // contract_value again — the marked figure moved to its own "Last Marked Margin
  // Value" column, and marked_contract_value is now null until the first mark. So
  // filter on booking contract_value to keep "filter what you see" (was LR-476's
  // marked twin). Native (security-ccy) basis; the cross-ccy native-vs-converted
  // gap is pre-existing (LR-474) and out of scope here.
  if (filters.contractValueMin !== null) {
    result = result.filter((item) => item.contract_value >= filters.contractValueMin!);
  }

  if (filters.contractValueMax !== null) {
    result = result.filter((item) => item.contract_value <= filters.contractValueMax!);
  }

  if (filters.termTypes.length > 0) {
    result = result.filter((item) => item.term_type && filters.termTypes.includes(item.term_type));
  }

  if (filters.currencies.length > 0) {
    // Case-insensitive: the currency pill stores UPPERCASE ISO codes (its list comes from
    // aggregateByCurrency, which uppercases), so a mixed/lower-case item.currency must
    // still match — otherwise picking that currency would filter out its own rows. The
    // sidebar's raw options (conventionally uppercase) keep matching once both sides upper.
    const wantedCurrencies = filters.currencies.map((c) => c.toUpperCase());
    result = result.filter((item) => wantedCurrencies.includes((item.currency || '').toUpperCase()));
  }

  if (filters.settlementCurrencies.length > 0) {
    // LR-474 (D3): the summary-bar currency pill filters by the EFFECTIVE
    // settlement currency (settlement_currency ?? currency), independent of the
    // sidebar's security-currency filter above. Same case-insensitive contract:
    // the pill stores uppercase keys from the settlement-ccy aggregation.
    const wanted = filters.settlementCurrencies.map((c) => c.toUpperCase());
    result = result.filter((item) => wanted.includes(getEffectiveSettlementCurrency(item)));
  }

  if (filters.marginMin !== null) {
    result = result.filter((item) => item.margin >= filters.marginMin!);
  }

  if (filters.marginMax !== null) {
    result = result.filter((item) => item.margin <= filters.marginMax!);
  }

  if (filters.maturityDateFrom) {
    result = result.filter((item) => item.maturity_date && item.maturity_date >= filters.maturityDateFrom!);
  }

  if (filters.maturityDateTo) {
    result = result.filter((item) => item.maturity_date && item.maturity_date <= filters.maturityDateTo!);
  }

  if (filters.grossPriceMin !== null) {
    result = result.filter((item) => item.gross_price >= filters.grossPriceMin!);
  }

  if (filters.grossPriceMax !== null) {
    result = result.filter((item) => item.gross_price <= filters.grossPriceMax!);
  }

  // LR-628: read the same figure the cell renders. `settlement-ccy` now prefers
  // `effective_daily_earnings` for every display consumer (cell, CSV, CSV
  // totals, MetricsBar), so comparing the raw booked field here would filter the
  // book on a number the trader cannot see: a row kept by "≥ 30" then renders
  // 23.15, and the row actually earning 35/day disappears. Native basis, not the
  // settlement twin — that is what this filter has always meant, and re-basing it
  // is a separate decision from LR-628.
  if (filters.dailyEarningsMin !== null) {
    result = result.filter((item) => {
      const daily = getNativeAmount(item, 'daily_earnings');
      return daily != null && daily >= filters.dailyEarningsMin!;
    });
  }

  if (filters.dailyEarningsMax !== null) {
    result = result.filter((item) => {
      const daily = getNativeAmount(item, 'daily_earnings');
      return daily != null && daily <= filters.dailyEarningsMax!;
    });
  }

  if (filters.createdAtFrom) {
    result = result.filter((item) => item.created_at >= filters.createdAtFrom!);
  }

  if (filters.createdAtTo) {
    result = result.filter((item) => item.created_at <= filters.createdAtTo! + 'T23:59:59');
  }

  if (filters.updatedAtFrom) {
    result = result.filter((item) => item.updated_at >= filters.updatedAtFrom!);
  }

  if (filters.updatedAtTo) {
    result = result.filter((item) => item.updated_at <= filters.updatedAtTo! + 'T23:59:59');
  }

  return result;
}
